import { SnackbarProvider, useSnackbar, type VariantType } from 'notistack';
import type { ReactNode } from 'react';

/**
 * One toast system for the whole portal.
 *
 * Legacy used react-toastify in three apps (configured differently in each) and
 * nothing at all in the fourth.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <SnackbarProvider
      maxSnack={3}
      autoHideDuration={4000}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      preventDuplicate
    >
      {children}
    </SnackbarProvider>
  );
}

export interface Toast {
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

/**
 * Errors stay on screen until dismissed. An auto-hiding error is an error the
 * user can miss, and in an approvals workflow that means silent data loss.
 */
export function useToast(): Toast {
  const { enqueueSnackbar } = useSnackbar();

  const show = (variant: VariantType, message: string) =>
    enqueueSnackbar(message, {
      variant,
      persist: variant === 'error',
      ...(variant === 'error' ? { autoHideDuration: null } : {}),
    });

  return {
    success: message => show('success', message),
    error: message => show('error', message),
    warning: message => show('warning', message),
    info: message => show('info', message),
  };
}
